import {
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import theme from "./theme";
import { Chart as ChartJS, ArcElement, Legend, Tooltip } from "chart.js";
import { ThemeProvider } from "@mui/material";
import globalStore from "./store/redux-store";
import { Provider } from "react-redux";

// layouts
import RootLayout from "./components/Layout/RootLayout";
import MainLayout from "./components/Layout/MainLayout";
import AuthLayout from "./components/Layout/AuthLayout";
import EventDetailLayout from "./components/Layout/EventDetailLayout";

// Pages
import Landing from "./pages/Landing";
import Signin from "./pages/Auth/Signin";
import Signup from "./pages/Auth/Signup";
import Otp from "./pages/Auth/Otp";
import Home from "./pages/Home";
import Guests from "./pages/Guests";
import NewGuests from "./pages/NewGuests";
import PhotographersPage from "./pages/PhotographersPage";
import QrcodePage from "./pages/QrcodePage";
import Albums from "./pages/AlbumsPage";
import Faces from "./pages/FacesPage";
import CommonLayout from "./components/Layout/CommonLayout";
import EventDetail from "./pages/EventDetailPage";
import Authorize from "./components/Authorize/Authorize";
import PhotosPage from "./pages/PhotosPage";
import QrCodeRegister from "./pages/QrCodeRegister";
import FileUploadLayout from "./components/Layout/FileUploadLayout";
import NoNetwork from "./pages/NoNetwork";
import NotFound from "./pages/NotFound";
import { Detector } from "react-detect-offline";
import { useEffect, useState } from "react";
import AnalyticsPage from "./pages/AnalyticsPage";
import Admin from "./pages/Admin/Admin";
import PaymentSuccess from "./pages/Payment/PaymentSuccess";
import PaymentFail from "./pages/Payment/PaymentFail";
import ShareAccess from "./pages/ShareAccessPage";
import SettingsPage from "./pages/SettingsPage";
import NewPhotos from "./pages/NewPhotos/NewPhotos";
import PremiumAlbumsPage from "./pages/Settings/PremiumAlbumsPage";
import SettingsLayout from "./components/Layout/SettingsLayout";
import PremiumImageCostPage from "./pages/Settings/PremiumImageCostPage";
import SubscriptionOrderHistoryPage from "./pages/Settings/SubscriptionOrderHistoryPage";
import HowFreeTrialWorks from "./pages/HowFreeTrialWorks";
import Profile from "./pages/Profile/Profile";
import UserDetailsLayout from "./components/Layout/UserDetailsLayout";
import MembershipPage from "./pages/MembershipPage";
import EditProfile from "./pages/EditProfilePage";
import EasyShareDirection from "./pages/EasyShareDirection/EasyShareDirection";
import LoginPage from "./pages/Login/LoginPage";
import PingLayout from "./components/Layout/PingLayout";
import UserRegistrationLayout from "./components/Layout/UserRegistrationLayout/UserRegistrationLayout";
import UserRegistration from "./pages/UserRegistration/UserRegistration";
import UserRegistrationPhotos from "./pages/UserPhotos/UserRegistrationPhotos";
import SelfiePage from "./pages/SelfiePage/SelfiePage";
import UserRegistrationLogin from "./pages/UserRegistrationLogin/UserRegistrationLogin";
import UserRegistrationAuthLayout from "./components/Layout/UserRegistrationAuthLayout/UserRegistrationAuthLayout";
import NewFileUploadLayout from "./components/Layout/NewFileUploadLayout/NewFileUploadLayout";
import NewFaceListPage from "./components/UI/NewFaceListPage/NewFaceListPage";
import GuestClusterListPage from "./pages/GuestClusterListPage/GuestClusterListPage";
import UserEventRegistration from "./pages/UserEventRegistration/UserEventRegistration";
import GTMInitializer from "./util/GTMInitializer";
import CreateEvent from "./pages/CreateEvent";
import JitoUserLandingPage from "./pages/JitoUserLandingPage/JitoUserLandingPage";
import FacesPage from "./pages/FacesPage/FacesPage";
import Recommendation from "./pages/Recommendation";

ChartJS.register(ArcElement, Tooltip, Legend);

const routes = (
  <>
    <Route element={<UserRegistrationLayout />} path="/user-registered">
      <Route element={<UserRegistrationLogin />} index />
      <Route element={<UserEventRegistration />} path="event-registration" />
      <Route element={<UserRegistrationAuthLayout />}>
        <Route element={<UserRegistration />} path="user-status" />
        <Route element={<SelfiePage />} path="profile-selfie" />
        <Route element={<UserRegistrationPhotos />} path="photos" />
      </Route>
    </Route>

    <Route element={<RootLayout />} path="/">
      <Route element={<FileUploadLayout />}>
        <Route element={<NewFileUploadLayout />}>
          <Route
            path="/profile"
            element={<Authorize Component={<Profile />} />}
          />
          <Route path="/login" element={<LoginPage />} />
          <Route element={<MainLayout />}>
            <Route
              path="/jito-user"
              element={<Authorize Component={<JitoUserLandingPage />} />}
            />
            <Route
              path="/create-event"
              element={<Authorize Component={<CreateEvent />} />}
            />{" "}
            {/* This layout has common header */}
            <Route index element={<Landing />} />
            <Route
              path="/how-free-trial-works"
              element={<HowFreeTrialWorks />}
            />{" "}
            <Route path="/sign-up" element={<QrCodeRegister />} />
            <Route path="/easy-share" element={<EasyShareDirection />} />
            <Route element={<PingLayout />}>
              <Route
                path="/home"
                element={<Authorize Component={<Home />} />}
              />
              <Route
                path="/userdetail"
                element={<Authorize Component={<UserDetailsLayout />} />}>
                <Route path="membership" element={<MembershipPage />} />
                <Route path="edit-profile" element={<EditProfile />} />
                <Route
                  path="order-history"
                  element={<SubscriptionOrderHistoryPage />}
                />
              </Route>
              <Route
                path="/home/events"
                element={<Authorize Component={<EventDetailLayout />} />}>
                <Route element={<CommonLayout />}>
                  <Route
                    element={<>{/* <EventDetail /> */}</>}
                    path="event-detail/:id"
                  />
                  <Route element={<Albums />} path="albums/:id" />
                  <Route element={<FacesPage />} path="faces/:id" />
                  <Route element={<FacesPage />} path="newfaces/:id" />
                  <Route element={<Recommendation />} path="recommendation/:id" />
                  <Route
                    element={<NewFaceListPage />}
                    path="faces/:id/face-list/:cid"
                  />
                  <Route
                    element={<GuestClusterListPage />}
                    path="guests/:id/guest-cluster-list/:gid"
                  />
                  <Route
                    element={<GuestClusterListPage />}
                    path="photographers/:id/photographer-cluster-list/:gid"
                  />
                  <Route
                    element={<NewPhotos />}
                    path="albums/:id/photos/:fid"
                  />
                  <Route
                    element={<NewPhotos />}
                    path="albums/:id/newphotos/:fid"
                  />
                  <Route element={<AnalyticsPage />} path="analytics/:id/" />
                  <Route element={<QrcodePage />} path="qr/:id/" />
                  <Route element={<ShareAccess />} path="share/:id/" />
                  <Route element={<SettingsLayout />} path="settings/:id">
                    <Route element={<SettingsPage />} index />
                    <Route
                      element={<PremiumAlbumsPage />}
                      path="premium-albums"
                    />
                    <Route
                      element={<PremiumImageCostPage />}
                      path="premium-image-cost"
                    />
                    {/* <Route
                element={<SubscriptionOrderHistoryPage />}
                path="order-history"
              /> */}
                  </Route>
                </Route>
                {/* <Route element={<Guests />} path="guests/:id" /> */}
                <Route element={<NewGuests />} path="guests/:id" />
                <Route element={<NewGuests />} path="newguests/:id" />
                <Route
                  element={<PhotographersPage />}
                  path="photographers/:id"
                />
              </Route>
            </Route>
            {/* admin page  */}
            <Route element={<Admin />} path="/sadmin" />
          </Route>
          <Route element={<AuthLayout />} path="/auth">
            <Route element={<Signin />} path="signin" />
            <Route element={<Signup />} path="signup" />
            <Route element={<Otp />} path="otp" />
          </Route>
        </Route>
      </Route>
      <Route element={<NotFound />} path="*" />
      <Route element={<PaymentSuccess />} path="/payment-success" />
      <Route element={<PaymentFail />} path="/payment-failure" />

      {/* user registered */}
    </Route>
  </>
);

const router = createBrowserRouter(createRoutesFromElements(routes));

const App = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    let timeInterval = setTimeout(() => {
      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);
    }, 2000);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(timeInterval);
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Provider store={globalStore}>
        <GTMInitializer />
        {isOnline ? <RouterProvider router={router} /> : <NoNetwork />}
      </Provider>
    </ThemeProvider>
  );
};

export default App;
